import React, { useState } from 'react';
import { Lock, Eye, EyeOff, Shield, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { API } from '../lib/api';

/**
 * ForceChangePasswordModal
 * ─────────────────────────────────────────────────────────────────────────────
 * Rendered immediately after login when the backend reports
 * `user.mustChangePassword === true`. The modal:
 *   • blocks all interaction with the app behind it
 *   • requires the user to enter current + new + confirm passwords
 *   • POSTs /api/auth/change-own-password
 *   • on success, clears the flag via AuthContext.clearMustChangePassword()
 *
 * Cannot be dismissed without changing the password (the only escape route
 * is the Logout button).
 */
export function ForceChangePasswordModal() {
  const { user, logout, clearMustChangePassword } = useAuth();
  const [current, setCurrent]     = useState('');
  const [next, setNext]           = useState('');
  const [confirm, setConfirm]     = useState('');
  const [showNext, setShowNext]   = useState(false);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [success, setSuccess]     = useState(false);

  if (!user || user.mustChangePassword !== true) return null;

  const strength = next.length >= 12 ? 'Strong' : next.length >= 8 ? 'Medium' : next.length > 0 ? 'Weak' : '';
  const strengthColor = { Strong: '#10b981', Medium: '#f59e0b', Weak: '#ef4444' }[strength] || 'var(--auth-input-border)';

  const submit = async (e) => {
    e.preventDefault();
    if (!current) { setError('Enter your current (default) password.'); return; }
    if (next.length < 6) { setError('New password must be at least 6 characters.'); return; }
    if (next !== confirm) { setError('Passwords do not match.'); return; }
    if (next === current) { setError('New password must be different from the current one.'); return; }
    setLoading(true);
    setError('');
    try {
      const res  = await fetch(`${API}/auth/change-own-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${user.token}` },
        body: JSON.stringify({ currentPassword: current, newPassword: next, confirmPassword: confirm })
      });
      // Robust response parsing — some deploy environments (Cloudflare,
      // nginx, ingress) return HTML error pages on 5xx. Don't blow up on
      // non-JSON: show the actual status + a snippet of the body so the
      // user can diagnose the deployment issue immediately.
      const rawText = await res.text();
      let data = {};
      try { data = rawText ? JSON.parse(rawText) : {}; } catch (_) { /* non-JSON */ }
      if (res.ok && data.success) {
        setSuccess(true);
        setTimeout(() => { clearMustChangePassword(); }, 1500);
      } else {
        const serverMsg = data.message || data.error;
        const bodySnippet = !serverMsg && rawText
          ? ` · Body: ${rawText.replace(/<[^>]+>/g, '').trim().slice(0, 140)}`
          : '';
        setError(`${serverMsg || 'Password change failed.'} (HTTP ${res.status})${bodySnippet}`);
      }
    } catch (err) {
      // Real network / CORS / DNS failure — surface the exact reason.
      setError(`Network error: ${err?.message || 'Failed to fetch'}. Verify the backend URL is reachable from this browser (mixed-content, CORS, or DNS).`);
    }
    setLoading(false);
  };

  return (
    <div
      data-testid="force-password-modal"
      style={{
        position: 'fixed', inset: 0, zIndex: 2000,
        background: 'rgba(0, 0, 0, 0.78)',
        backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20, fontFamily: 'var(--font-sans, sans-serif)'
      }}
    >
      <div
        style={{
          width: '100%', maxWidth: 460,
          background: 'var(--auth-card-bg, #1e293b)',
          border: '1px solid var(--auth-card-border, #334155)',
          borderRadius: 18, padding: 32,
          boxShadow: '0 32px 64px rgba(0, 0, 0, 0.5)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
          <div style={{
            width: 44, height: 44, borderRadius: 12,
            background: 'linear-gradient(135deg, #f59e0b, #d97706)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 0 24px rgba(245, 158, 11, 0.4)'
          }}>
            <Shield size={20} color="#fff" />
          </div>
          <div>
            <h2 style={{ fontSize: 17, fontWeight: 900, color: 'var(--auth-text, #f8fafc)', margin: 0 }}>
              Password Change Required
            </h2>
            <p style={{ fontSize: 11, color: 'var(--auth-text-muted, #94a3b8)', margin: '4px 0 0', fontWeight: 600 }}>
              Welcome, {user.username} — let's secure your account
            </p>
          </div>
        </div>

        <div style={{
          background: 'rgba(245, 158, 11, 0.1)',
          border: '1px solid rgba(245, 158, 11, 0.3)',
          borderRadius: 10, padding: '12px 14px', marginBottom: 20,
          display: 'flex', alignItems: 'flex-start', gap: 10
        }}>
          <AlertTriangle size={14} color="#f59e0b" style={{ marginTop: 2, flexShrink: 0 }} />
          <div style={{ fontSize: 11, lineHeight: 1.55, color: 'var(--auth-text-muted, #cbd5e1)' }}>
            You are signed in with the system-provisioned default password.
            Please choose a new one. You won't be able to access any module until this is done.
          </div>
        </div>

        {success ? (
          <div data-testid="force-pwd-success" style={{ textAlign: 'center', padding: '20px 0' }}>
            <CheckCircle2 size={42} color="#10b981" style={{ marginBottom: 14 }} />
            <div style={{ color: '#10b981', fontWeight: 800, fontSize: 15 }}>Password Updated</div>
            <div style={{ color: 'var(--auth-text-muted)', fontSize: 11, marginTop: 6 }}>
              Continuing into the application…
            </div>
          </div>
        ) : (
          <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label className="auth-label">Current Password</label>
              <input
                type="password"
                value={current}
                onChange={e => setCurrent(e.target.value)}
                className="auth-input"
                placeholder="Enter your default password"
                data-testid="force-pwd-current"
                autoFocus
              />
            </div>

            <div>
              <label className="auth-label">New Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showNext ? 'text' : 'password'}
                  value={next}
                  onChange={e => setNext(e.target.value)}
                  className="auth-input"
                  placeholder="At least 6 characters"
                  style={{ paddingRight: 44 }}
                  data-testid="force-pwd-new"
                />
                <button
                  type="button"
                  onClick={() => setShowNext(p => !p)}
                  className="auth-show-pass-btn"
                  tabIndex={-1}
                >
                  {showNext ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              {strength && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                  <div style={{ flex: 1, height: 3, background: 'var(--auth-input-bg)', borderRadius: 2, overflow: 'hidden' }}>
                    <div style={{
                      width: strength === 'Strong' ? '100%' : strength === 'Medium' ? '65%' : '30%',
                      height: '100%', background: strengthColor,
                      borderRadius: 2, transition: 'width 300ms'
                    }} />
                  </div>
                  <span style={{ fontSize: 10, fontWeight: 700, color: strengthColor }}>{strength}</span>
                </div>
              )}
            </div>

            <div>
              <label className="auth-label">Confirm New Password</label>
              <input
                type="password"
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                className={`auth-input ${confirm && confirm !== next ? 'error' : ''}`}
                placeholder="Re-type the new password"
                data-testid="force-pwd-confirm"
              />
            </div>

            {error && (
              <div className="auth-error-banner" data-testid="force-pwd-error">{error}</div>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
              <button
                type="button"
                onClick={logout}
                className="btn btn-outline"
                style={{ flex: '0 0 110px', justifyContent: 'center' }}
                data-testid="force-pwd-logout"
              >
                Logout
              </button>
              <button
                type="submit"
                disabled={loading}
                className="auth-submit"
                style={{ flex: 1 }}
                data-testid="force-pwd-submit"
              >
                {loading ? 'Updating…' : <><Lock size={13} /> Update Password &amp; Continue</>}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default ForceChangePasswordModal;
